import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import PageWrapper from './PageWrapper.tsx';

interface LegalPageLayoutProps {
  title: string;
  lastUpdated: string;
  children: React.ReactNode;
}

const LegalPageLayout = ({ title, lastUpdated, children }: LegalPageLayoutProps) => {
  const location = useLocation();

  useEffect(() => {
    const originalTitle = document.title;
    document.title = `${title} | R-MAX Precificação Inteligente | RESOLV MAX`;

    const fullCanonicalUrl = `https://precificacao.resolvmax.com.br${location.pathname}`;
    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const prevCanonical = linkCanonical ? linkCanonical.href : '';
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.rel = 'canonical';
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.href = fullCanonicalUrl;

    return () => {
      document.title = originalTitle;
      if (linkCanonical) linkCanonical.href = prevCanonical;
    };
  }, [title, location.pathname]);
  return (
    <PageWrapper className="min-h-screen bg-slate-50 text-slate-800 py-12 px-4 sm:px-6 lg:px-8 font-sans selection:bg-emerald-500/20">
      <div className="max-w-3xl mx-auto">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-slate-600 hover:text-emerald-600 transition-colors mb-8 group font-medium focus-visible:ring-2 focus-visible:ring-emerald-500 outline-none rounded px-2"
          aria-label="Voltar para a página inicial"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" aria-hidden="true" />
          Voltar para a página inicial
        </Link>

        <div className="bg-white rounded-3xl shadow-sm p-8 md:p-12 border border-slate-200">
          <div className="mb-10 border-b border-slate-200 pb-6">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3 tracking-tight">{title}</h1>
            <p className="text-sm text-slate-500">Última atualização: {lastUpdated}</p>
          </div>
          
          <div className="space-y-8 text-slate-600 leading-relaxed font-normal">
            {children}
          </div>
          
          <div className="mt-16 pt-8 border-t border-slate-200 text-center">
            <Link to="/" className="text-emerald-600 hover:text-emerald-700 transition-colors font-semibold flex items-center justify-center gap-2">
              &larr; Voltar para a página inicial
            </Link>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default LegalPageLayout;
